import React, { useCallback, useEffect, useRef } from 'react';
import type { ToastPosition } from 'react-toastify';
import { Slide, toast, ToastContainer, Zoom } from 'react-toastify';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';

import AlertBanner from '~/components/core/AlertBanner';
import FsIconButton from '~/components/core/FsWrappers/FsIconButton/FsIconButton';
import type { ResolveItemPath } from '~/components/hooks/traverseErrors';
import { traverseErrors } from '~/components/hooks/traverseErrors';
import CloseIcon from '~/components/icons/CloseIcon';

const DEFAULT_TOAST_ID = 'form-errors';
const DEFAULT_TOAST_CONTAINER_CLASSNAME = 'w-[550px] bottom-80 border-none';
const DEFAULT_TITLE = 'Form validation errors:';

interface ValidationErrorToastContainerProps {
  containerId?: string;
  position?: ToastPosition;
  className?: string;
}

const ValidationErrorToastContainer: React.FC<ValidationErrorToastContainerProps> = ({
  containerId = DEFAULT_TOAST_ID,
  position = toast.POSITION.BOTTOM_CENTER,
  className = DEFAULT_TOAST_CONTAINER_CLASSNAME,
}) => (
  <ToastContainer
    draggable={false}
    closeButton
    transition={Zoom}
    limit={1}
    position={position}
    className={className}
    newestOnTop
    containerId={containerId}
  />
);

export const useFormikValidationErrorToast = ({
  toastId = DEFAULT_TOAST_ID,
  toastPosition = toast.POSITION.BOTTOM_CENTER,
  toastClassName,
  title = DEFAULT_TITLE,
  closeOnClick = false,
  resolveItemPath,
}: {
  toastId?: string;
  toastPosition?: ToastPosition;
  toastClassName?: string;
  title?: string;
  closeOnClick?: boolean;
  resolveItemPath?: ResolveItemPath;
}) => {
  const { errors, values, submitCount, isSubmitting } = useFormikContext();
  const lastShownErrorsRef = useRef<object | null>(null);
  const prevSubmitCountRef = useRef<number>(submitCount || 0);

  const toastValidationErrors = useCallback(() => {
    if (isEmpty(errors)) {
      if (toast.isActive(toastId)) {
        toast.dismiss(toastId);
      }
      return;
    }

    const errorsHaveChanged = JSON.stringify(lastShownErrorsRef.current) !== JSON.stringify(errors);
    if (!errorsHaveChanged && toast.isActive(toastId)) {
      return;
    }

    lastShownErrorsRef.current = errors;

    const toastContent = (
      <AlertBanner
        alertType={AlertBanner.ALERT_TYPES.ERROR}
        title={title}
        note={
          <ul>
            {/* Keyed on the index as well as the line: item labels come from `desc`/`display_name`,
                neither of which is validated for uniqueness, so two same-named items failing the same
                rule produce byte-identical lines. The index used to disambiguate them and no longer does. */}
            {traverseErrors(errors, [], values, resolveItemPath).map((error, index) => (
              <li key={`${index}-${error}`}>{`${error}.`}</li>
            ))}
          </ul>
        }
      />
    );

    if (toast.isActive(toastId)) {
      toast.update(toastId, {
        render: toastContent,
      });
    } else {
      toast(toastContent, {
        closeButton: ({ closeToast }) => (
          <div className="absolute right-16 top-16">
            <FsIconButton onClick={closeToast} icon={CloseIcon} ignorePermissions aria-label="Close" />
          </div>
        ),
        position: toastPosition,
        hideProgressBar: true,
        toastId,
        className: toastClassName,
        autoClose: false,
        closeOnClick,
        transition: Slide,
      });
    }
  }, [errors, values, toastId, toastPosition, toastClassName, title, closeOnClick, resolveItemPath]);

  useEffect(() => {
    if (submitCount < prevSubmitCountRef.current) {
      // The form was reset (resetForm zeroes submitCount), so the errors the toast lists no longer stand.
      prevSubmitCountRef.current = submitCount;
      lastShownErrorsRef.current = null;
      if (toast.isActive(toastId)) {
        toast.dismiss(toastId);
      }
      return;
    }
    if (submitCount > prevSubmitCountRef.current) {
      // https://formik.org/docs/guides/form-submission
      // Formik submitCount is updated after isSubmitting is set to true
      // isSubmitting is set to false after the form is submitted or on error
      if (!isSubmitting) {
        toastValidationErrors();
        prevSubmitCountRef.current = submitCount;
      }
    }
  }, [submitCount, toastValidationErrors, isSubmitting, toastId]);

  return {};
};

export { ValidationErrorToastContainer };
